import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usersAPI } from '../../services/api';
import { Save, X, Eye, EyeOff } from 'lucide-react';
import Toast from '../../components/Toast';

const UserForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        role: 'admin',
        active: true,
    });

    useEffect(() => {
        if (isEdit) {
            fetchUser();
        }
    }, [id]);

    const fetchUser = async () => {
        try {
            setLoading(true);
            const response = await usersAPI.getById(id);
            const user = response.data;
            setFormData({
                username: user.username,
                email: user.email,
                password: '', // Don't populate password for security
                role: user.role,
                active: user.active,
            });
        } catch (error) {
            console.error('Error fetching user:', error);
            showToast('Failed to load user', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!isEdit && !formData.password) {
            showToast('Password is required for new users', 'error');
            return;
        }

        if (formData.password && formData.password.length < 6) {
            showToast('Password must be at least 6 characters', 'error');
            return;
        }

        setLoading(true);

        try {
            const userData = {
                username: formData.username,
                email: formData.email,
                role: formData.role,
                active: formData.active,
            };

            // Only include password if it's provided
            if (formData.password) {
                userData.password = formData.password;
            }

            if (isEdit) {
                await usersAPI.update(id, userData);
                showToast('User updated successfully', 'success');
            } else {
                await usersAPI.create(userData);
                showToast('User created successfully', 'success');
            }

            setTimeout(() => navigate('/admin/users'), 1500);
        } catch (error) {
            console.error('Error saving user:', error);
            const message = error.response?.data?.message || 'Failed to save user';
            showToast(message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type) => {
        setToast({ message, type });
    };

    if (loading && isEdit) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-earth-green"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900">
                    {isEdit ? 'Edit User' : 'Add New User'}
                </h1>
                <p className="text-gray-600 mt-1">
                    {isEdit ? 'Update user information and access' : 'Create a new admin panel user'}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* User Information */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">User Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Username *
                            </label>
                            <input
                                type="text"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email *
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Password {!isEdit && '*'}
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required={!isEdit}
                                    minLength={6}
                                    placeholder={isEdit ? 'Enter new password to reset' : 'Enter password'}
                                    className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                            {isEdit ? (
                                <p className="text-xs text-gray-500 mt-1">
                                    Leave blank to keep current password, or enter a new password to reset it
                                </p>
                            ) : (
                                <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Role *
                            </label>
                            <select
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            >
                                <option value="admin">Admin</option>
                                <option value="super-admin">Super Admin</option>
                                <option value="employee">Employee</option>
                            </select>
                        </div>

                        <div className="md:col-span-2">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="active"
                                    checked={formData.active}
                                    onChange={handleChange}
                                    className="h-4 w-4 text-earth-green focus:ring-earth-green border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm text-gray-700">
                                    Active (user can access admin panel)
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/users')}
                        className="btn-outline inline-flex items-center space-x-2"
                    >
                        <X className="h-5 w-5" />
                        <span>Cancel</span>
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
                    >
                        <Save className="h-5 w-5" />
                        <span>{loading ? 'Saving...' : isEdit ? 'Update User' : 'Create User'}</span>
                    </button>
                </div>
            </form>

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default UserForm;
